import type { ListBuilderConfiguration } from '../types';

/**
 * Order line items shown inside the order list's row expansion (PD-15221).
 *
 * Rendered as an embedded list when an order row is expanded on the main
 * Orders page. Data comes from GET /orders/:id/details via the module's
 * rowExpansionQuery callback (which normalizes itemId / itemNumber the same
 * way the Order Details line-items card does) — rows exist only for the
 * expanded order; this is NOT a standalone page list.
 *
 * Own listKey (not `order_detail`) so customizing the expansion can never
 * restyle the Order Details page's line-items card — saved-search
 * customizations are layered per listKey.
 *
 * Default columns mirror the quote expansion (item, item number, quantity
 * ordered, client-scoped quantity on hand); shipped/backordered and the
 * money fields are selectable but off by default, per the optional-field
 * convention.
 */
export const ORDER_EXPANSION_LINES_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-order-expansion-lines',
			cacheTTL: 600000,
			name: 'Order Line Items',
			type: 'default',
			active: true,
			sortOrder: 0,
			defaultSortId: 'line-number-asc',
			columns: [
				{
					id: 'item',
					headers: [{ id: 'item-header', text: 'Item' }],
					fields: [
						{
							id: 'item-description-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'itemDescription',
									objectTable: 'order_detail',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: { type: 'theme', themeColor: 'link' },
								fontWeight: 'semibold',
							},
							link: {
								enabled: true,
								type: 'object',
								objectTable: 'item',
								idField: 'itemId',
							},
							displayStyle: 'text',
						},
					],
					sortable: true,
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'itemDescription',
							objectTable: 'order_detail',
						},
					],
					alignment: 'left',
					minWidth: 250,
					widthConfig: { mode: 'flexible', preset: 'large' },
					verticalAlignment: 'middle',
				},
				{
					id: 'item-number',
					headers: [{ id: 'item-number-header', text: 'Item #' }],
					fields: [
						{
							id: 'item-number-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'itemNumber' },
							},
							styleConfig: {
								size: 'md',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
					],
					sortable: true,
					sortFields: [{ direction: 'ASC', fieldName: 'itemNumber' }],
					alignment: 'left',
					minWidth: 120,
					widthConfig: { mode: 'flexible', preset: 'medium' },
					verticalAlignment: 'middle',
				},
				{
					id: 'qty-ordered',
					headers: [{ id: 'qty-ordered-header', text: 'Qty Ordered' }],
					fields: [
						{
							id: 'qty-ordered-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'quantityOrdered',
									objectTable: 'order_detail',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: { type: 'theme', themeColor: 'textPrimary' },
								fontWeight: 'semibold',
								formatOptions: { numberFormat: 'auto' },
							},
							displayStyle: 'number',
						},
					],
					sortable: true,
					sortFields: [
						{
							direction: 'DESC',
							fieldName: 'quantityOrdered',
							objectTable: 'order_detail',
						},
					],
					alignment: 'right',
					minWidth: 100,
					widthConfig: { mode: 'flexible', preset: 'small' },
					verticalAlignment: 'middle',
				},
				{
					id: 'qty-on-hand',
					headers: [{ id: 'qty-on-hand-header', text: 'Qty On Hand' }],
					fields: [
						{
							id: 'qty-on-hand-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'quantityOnHand' },
							},
							styleConfig: {
								size: 'md',
								colorRule: { type: 'theme', themeColor: 'textPrimary' },
								fontWeight: 'semibold',
								formatOptions: { numberFormat: 'auto' },
							},
							displayStyle: 'number',
						},
					],
					sortable: true,
					sortFields: [{ direction: 'DESC', fieldName: 'quantityOnHand' }],
					alignment: 'right',
					minWidth: 100,
					widthConfig: { mode: 'flexible', preset: 'small' },
					verticalAlignment: 'middle',
				},
			],
			description: 'Line items shown when an order row is expanded',
			requiredFields: [
				'order_detail._id',
				'order_detail._fk_item__id',
				'order_detail.lineNumber',
			],
		},
	],
	paging: {
		defaultPageSize: 50,
		pageSizeOptions: [25, 50, 100],
	},
	filters: {
		activeFilters: [],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/items/:id',
			objectLabel: 'Item',
			objectTable: 'item',
			selectedFields: [],
		},
		{
			baseUrl: '/orders/:orderId',
			objectLabel: 'Order Line Item',
			objectTable: 'order_detail',
			selectedFields: [
				{ fieldName: '_id', fieldLabel: 'ID' },
				{ fieldName: 'lineNumber', fieldLabel: 'Line Item' },
				{ fieldName: 'itemNumber', fieldLabel: 'Item #' },
				{ fieldName: 'itemDescription', fieldLabel: 'Description' },
				{ fieldName: 'manufacturer', fieldLabel: 'Manufacturer' },
				{ fieldName: 'itemNDC', fieldLabel: 'NDC' },
				{ fieldName: 'size', fieldLabel: 'Size' },
				{ fieldName: 'strength', fieldLabel: 'Strength' },
				{ fieldName: 'price', fieldLabel: 'Price' },
				{ fieldName: 'quantityOrdered', fieldLabel: 'Qty Ordered' },
				{ fieldName: 'quantityShipped', fieldLabel: 'Qty Shipped' },
				{ fieldName: 'quantityBackordered', fieldLabel: 'Qty Backordered' },
				// Summed across the order client's warehouses (client_warehouse
				// scoping) by OrdersService.getOrderDetails.
				{ fieldName: 'quantityOnHand', fieldLabel: 'Qty On Hand' },
				{ fieldName: 'subtotal', fieldLabel: 'Subtotal' },
				{ fieldName: 'total', fieldLabel: 'Total' },
			],
		},
	],
	tableSorts: [
		{
			id: 'line-number-asc',
			name: 'Line Number',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'lineNumber',
					objectTable: 'order_detail',
				},
			],
			description: 'Sort by line number',
		},
		{
			id: 'description-asc',
			name: 'Description',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'itemDescription',
					objectTable: 'order_detail',
				},
			],
			description: 'Sort by item description',
		},
		{
			id: 'qty-on-hand-desc',
			name: 'Qty On Hand',
			fields: [{ direction: 'DESC', fieldName: 'quantityOnHand' }],
			description: 'Sort by quantity on hand (highest first)',
		},
	],
};
